import React, { useState, useEffect, Children, isValidElement, cloneElement } from 'react'
import { TabView, TabViewProps, TabPanel, TabViewTabChangeEvent, TabPanelProps } from 'primereact/tabview'

const getTabKey = (props: TabPanelProps, index: number): string => {
    return `${props.header?.toString()}-${index}`
}

const LazyTabView: React.FC<TabViewProps> = ({ children, onTabChange, activeIndex: externalActiveIndex, ...props }) => {
    const validChildren = Children.toArray(children).filter((child) => isValidElement(child) && child.type === TabPanel)

    const [internalActiveIndex, setInternalActiveIndex] = useState(0)
    const requestedActiveIndex = externalActiveIndex ?? internalActiveIndex
    const activeIndex =
        validChildren.length > 0
            ? Math.min(
                  Math.max(Number.isFinite(requestedActiveIndex) ? requestedActiveIndex : 0, 0),
                  validChildren.length - 1
              )
            : 0
    const [renderedTabs, setRenderedTabs] = useState<Set<string>>(() => {
        const initialTabs = new Set<string>()

        if (validChildren.length > 0 && isValidElement<TabPanelProps>(validChildren[0])) {
            initialTabs.add(getTabKey(validChildren[0].props, 0))
        }

        const activeChild = validChildren[activeIndex]
        if (isValidElement<TabPanelProps>(activeChild)) {
            initialTabs.add(getTabKey(activeChild.props, activeIndex))
        }

        return initialTabs
    })

    useEffect(() => {
        if (validChildren.length === 0) return

        if (activeIndex !== requestedActiveIndex && externalActiveIndex === undefined) {
            setInternalActiveIndex(activeIndex)
        }

        const child = validChildren[activeIndex]
        if (isValidElement<TabPanelProps>(child)) {
            const tabKey = getTabKey(child.props, activeIndex)
            setRenderedTabs((currentTabs) => {
                if (currentTabs.has(tabKey)) return currentTabs

                return new Set(currentTabs).add(tabKey)
            })
        }
    }, [activeIndex, externalActiveIndex, requestedActiveIndex, validChildren])

    const handleTabChange = (e: TabViewTabChangeEvent) => {
        const child = validChildren[e.index]
        if (isValidElement<TabPanelProps>(child)) {
            const tabKey = getTabKey(child.props, e.index)
            setRenderedTabs((currentTabs) => new Set(currentTabs).add(tabKey))
        }
        if (externalActiveIndex === undefined) {
            setInternalActiveIndex(e.index)
        }
        onTabChange?.(e)
    }

    const processedChildren = validChildren.map((child, index) => {
        if (isValidElement<TabPanelProps>(child)) {
            const tabKey = getTabKey(child.props, index)
            if (!renderedTabs.has(tabKey)) {
                return cloneElement<TabPanelProps>(child, { children: null })
            }
        }
        return child
    })

    return (
        <TabView {...props} activeIndex={activeIndex} onTabChange={handleTabChange} renderActiveOnly={false} scrollable>
            {processedChildren}
        </TabView>
    )
}

export { LazyTabView }
